// =============================================================
// Spares booking math + anti-fraud caps — pure logic.
//
// Ported from the prototype (scripts/perpetuum-device-card.jsx):
// "booked = reserved against open work orders; available = stock − booked",
// low when `available < min`.
//
// Binding domain rules (PROJECT.md): "spares booking math
// (Available = In Stock − Booked)"; anti-fraud "write-off non-negative cap".
// =============================================================

import type { StockLevel } from "../types/index.js";

/** Available = In Stock − Booked. Never negative (clamped at 0). */
export function availableQty(inStock: number, booked: number): number {
  return Math.max(0, inStock - booked);
}

/** Convenience over a StockLevel row. */
export function availableForLevel(level: Pick<StockLevel, "inStock" | "booked">): number {
  return availableQty(level.inStock, level.booked);
}

/** A spare is "low" when Available is at or below its reorder minimum. */
export function isLowStock(inStock: number, booked: number, minQty: number): boolean {
  return availableQty(inStock, booked) < minQty;
}

/**
 * Can `qty` more be booked? Only what is Available may be reserved.
 * Prevents over-booking against on-hand stock.
 */
export function canBook(inStock: number, booked: number, qty: number): boolean {
  if (qty <= 0) return false;
  return qty <= availableQty(inStock, booked);
}

/**
 * Apply a booking, returning the new `booked` count.
 * Throws if it would exceed on-hand stock (anti-fraud: no phantom reservations).
 */
export function applyBooking(inStock: number, booked: number, qty: number): number {
  if (qty <= 0) throw new Error("applyBooking: qty must be > 0");
  if (!canBook(inStock, booked, qty)) {
    throw new Error(
      `applyBooking: cannot book ${qty} — only ${availableQty(inStock, booked)} available`,
    );
  }
  return booked + qty;
}

/** Release a prior booking, returning the new `booked` count (never below 0). */
export function releaseBooking(booked: number, qty: number): number {
  if (qty <= 0) throw new Error("releaseBooking: qty must be > 0");
  return Math.max(0, booked - qty);
}

/**
 * Apply a write-off / consumption against on-hand stock.
 * Anti-fraud non-negative cap: you can never write off more than is in stock.
 * Returns the new `inStock` count.
 */
export function applyWriteOff(inStock: number, qty: number): number {
  if (qty <= 0) throw new Error("applyWriteOff: qty must be > 0");
  if (qty > inStock) {
    throw new Error(`applyWriteOff: cannot write off ${qty} — only ${inStock} in stock`);
  }
  return inStock - qty;
}

/**
 * Apply a stock-to-stock transfer (booking-aware), returning the new on-hand
 * counts at both warehouses. Total on-hand is conserved — only what is
 * Available at the source (in_stock − booked) may move; reserved (booked) stock
 * stays put. Throws if the move would dip into reserved stock (anti-fraud).
 */
export function applyTransfer(
  from: { inStock: number; booked: number },
  toInStock: number,
  qty: number,
): { fromInStock: number; toInStock: number } {
  if (qty <= 0) throw new Error("applyTransfer: qty must be > 0");
  const movable = availableQty(from.inStock, from.booked);
  if (qty > movable) {
    throw new Error(
      `applyTransfer: cannot transfer ${qty} — only ${movable} available (reserved stock stays put)`,
    );
  }
  return { fromInStock: from.inStock - qty, toInStock: toInStock + qty };
}
